// Iter69 — Shared "Copy JSON" + "Download Snapshot" affordance for
// every module scanner panel (BPCS, BCCS, Iron Condor, CFM-CC-SHARES,
// CFM-CC-LEAPS) and the Wheel ITM visibility passthrough.
//
// HARD CONSTRAINTS:
//  * Pure browser-local. Zero outbound HTTP traffic — no axios
//    import, no fetch call, no XHR object, no beacon API usage.
//  * Copy uses navigator.clipboard.writeText only.
//  * Download builds a Blob + object URL + anchor click, then revokes.
//  * Snapshot envelope is hard-coded read-only / no-execution / no-
//    journal / no-broker. CFM-CC-LEAPS adds two extra disclosure
//    flags (assignment_automation_enabled, exercise_automation_enabled)
//    via the optional `extraFlags` prop.
//  * Renders Nothing when there is no result (caller is responsible
//    for gating mount on `result && !loading && !err`).

import React, { useState } from "react";
import { Copy, Download } from "lucide-react";

const SNAPSHOT_VERSION = "iter69_v1";
const SAFE_NAME_RE = /[^A-Z0-9._-]/g;

function safeName(s) {
  return String(s || "UNKNOWN").toUpperCase().replace(SAFE_NAME_RE, "_");
}

function safeTimestamp() {
  return new Date().toISOString().replace(/[:.]/g, "-");
}

function pickBatchOrSymbol(result) {
  const recs = Array.isArray(result?.results) ? result.results : [];
  if (recs.length === 1 && recs[0]?.symbol) {
    return safeName(recs[0].symbol);
  }
  // Wheel passthrough single-symbol shape uses `symbol` at the root.
  if (recs.length === 0 && result?.symbol) {
    return safeName(result.symbol);
  }
  return "batch";
}

export default function ScannerResultActions({
  result,
  request,
  endpoint,
  sourcePanel,
  moduleId,
  extraFlags = null,
  testidPrefix,
}) {
  const [copyState, setCopyState] = useState("idle");
  const [downloadState, setDownloadState] = useState("idle");

  if (!result) return null;

  async function copyResultJson() {
    try {
      const text = JSON.stringify(result, null, 2);
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch (e) {
      setCopyState("failed");
    } finally {
      setTimeout(() => setCopyState("idle"), 2000);
    }
  }

  function downloadSnapshot() {
    try {
      const snapshot = {
        snapshot_version: SNAPSHOT_VERSION,
        created_at: new Date().toISOString(),
        source_panel: sourcePanel,
        module_id: moduleId,
        scanner_endpoint: endpoint,
        request_payload_used: request ?? null,
        response_result: result,
        read_only: true,
        execution_enabled: false,
        journal_mutation_enabled: false,
        broker_execution_enabled: false,
        ...(extraFlags || {}),
        disclaimer:
          "Read-only scanner snapshot — not financial advice. This " +
          "file is a local browser artifact only. No backend " +
          "persistence, no journal write, no broker order placement, " +
          "no automatic trade execution, no exercise automation, and " +
          "no assignment automation was performed.",
      };
      const safeMod = safeName(moduleId);
      const safeSym = pickBatchOrSymbol(result);
      const ts = safeTimestamp();
      const filename =
        `arxmentis_scanner_snapshot_${safeMod}_${safeSym}_${ts}.json`;
      const blob = new Blob(
        [JSON.stringify(snapshot, null, 2)],
        { type: "application/json" },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadState("downloaded");
    } catch (e) {
      setDownloadState("failed");
    } finally {
      setTimeout(() => setDownloadState("idle"), 2000);
    }
  }

  // Idle / copied / failed pills.
  let copyBtn;
  if (copyState === "copied") {
    copyBtn = (
      <button
        type="button"
        data-testid={`${testidPrefix}-copy-json-success`}
        onClick={copyResultJson}
        className="px-2 py-0.5 rounded-sm border border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88] text-[9px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1"
      >
        ✓ Copied
      </button>
    );
  } else if (copyState === "failed") {
    copyBtn = (
      <button
        type="button"
        data-testid={`${testidPrefix}-copy-json-failed`}
        onClick={copyResultJson}
        className="px-2 py-0.5 rounded-sm border border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366] text-[9px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1"
      >
        ⚠ Copy failed
      </button>
    );
  } else {
    copyBtn = (
      <button
        type="button"
        data-testid={`${testidPrefix}-copy-json`}
        onClick={copyResultJson}
        className="px-2 py-0.5 rounded-sm border border-[#26262B] hover:border-[#3F3F47] text-[#8A8A93] hover:text-[#E0E0E0] text-[9px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1"
      >
        <Copy size={9} /> Copy JSON
      </button>
    );
  }

  let downloadBtn;
  if (downloadState === "downloaded") {
    downloadBtn = (
      <button
        type="button"
        data-testid={`${testidPrefix}-download-snapshot-success`}
        onClick={downloadSnapshot}
        className="px-2 py-0.5 rounded-sm border border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88] text-[9px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1"
      >
        ✓ Downloaded
      </button>
    );
  } else if (downloadState === "failed") {
    downloadBtn = (
      <button
        type="button"
        data-testid={`${testidPrefix}-download-snapshot-failed`}
        onClick={downloadSnapshot}
        className="px-2 py-0.5 rounded-sm border border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366] text-[9px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1"
      >
        ⚠ Download failed
      </button>
    );
  } else {
    downloadBtn = (
      <button
        type="button"
        data-testid={`${testidPrefix}-download-snapshot`}
        onClick={downloadSnapshot}
        className="px-2 py-0.5 rounded-sm border border-[#26262B] hover:border-[#3F3F47] text-[#8A8A93] hover:text-[#E0E0E0] text-[9px] font-mono uppercase tracking-[0.12em] inline-flex items-center gap-1"
      >
        <Download size={9} /> Download Snapshot
      </button>
    );
  }

  return (
    <span
      data-testid={`${testidPrefix}-actions`}
      className="inline-flex items-center gap-1"
    >
      {copyBtn}
      {downloadBtn}
    </span>
  );
}
